import type { RoofingServiceContent } from "@/components/RoofingServicePage";

export const solarRepairPages: Record<string, RoofingServiceContent> = {
  "solar-repair": {
    type: "solar",
    currentSlug: "solar-repair",
    heroTitle: "Solar Repair in Southern California",
    heroSubtitle: "Inverter not working? We'll replace it with a manufacturer warranty and become your ongoing service provider - plus full diagnostics and repair for the rest of the system.",
    intro: "A solar system that's stopped producing right is almost always fixable. We run real diagnostics, find the actual cause, and repair it - not just guess and swap parts.",
    sections: [
      {
        title: "Inverter not working? We'll replace it and stick around",
        body: [
          "The inverter is the piece that fails most, and it's the piece that fails hardest - when it goes down, production usually drops to zero, not just a little. If yours is dead or throwing constant errors, we'll replace it with a manufacturer warranty behind it, not a rebuilt unit off a truck.",
          "After the swap, we become your ongoing service provider. That means one company to call going forward instead of chasing whoever installed the system originally, especially if that company isn't around anymore.",
        ],
      },
      {
        title: "What we diagnose before touching anything",
        body: [
          "We pull your monitoring data first and compare it against what the system should be producing for the time of year. That tells us whether we're chasing a total failure, a partial string issue, or a slow decline.",
          "Then we get on the roof and check the array itself - panel by panel, connector by connector - along with the inverter, disconnects, and roof penetrations around the mounting points. We don't recommend a repair until we know exactly what's wrong.",
        ],
      },
      {
        title: "Common causes we find",
        body: [
          "Inverter failure is the number one call we get - it's an electronic component sitting in a hot attic or on an exterior wall, and it has a service life. String faults show up as one section of the array underperforming while the rest looks normal.",
          "Rodent damage is more common than people expect - rats and squirrels chew wire insulation under panels where it's dark and sheltered, and that can cause anything from a nuisance fault to a real fire risk. Roof penetration leaks happen when the flashing around a mounting foot was never sealed right or has aged out over the years.",
          "We also see monitoring going offline on its own - the system can still be producing fine, but if you can't see it, you can't catch a real problem early. And plain panel degradation, where output has just faded year over year past what's normal for the panel's age.",
        ],
      },
      {
        title: "What a service visit includes",
        body: [
          "A full monitoring data review, a physical inspection of the array and mounting hardware, testing at the inverter and combiner, and a check of every accessible connector and roof penetration.",
          "We give you a straight explanation of what's wrong, what it costs to fix, and what your options are - repair, partial replacement, or full inverter swap. No pressure into anything the system doesn't need.",
        ],
      },
      {
        title: "We take over systems from companies that are gone",
        body: [
          "A big share of our repair calls are on solar systems installed by companies that went out of business or stopped answering the phone. Solar has had a lot of installers come and go in Southern California over the years, and it leaves homeowners with a warranty that's worthless and no one to call.",
          "We'll service what's already on your roof regardless of who put it there. If parts need replacing, we source what's compatible and get you a real warranty on the new work going forward.",
        ],
      },
      {
        title: "Where we work",
        body: [
          "We handle solar repair across Southern California, Coronado, Winter Gardens, Pine Valley, Poway, Carlsbad and the rest of Southern California. Same crew that installs solar also handles the repairs, so nothing about the roofing side gets missed.",
        ],
      },
    ],
  },

  "solar-inverter-repair": {
    type: "solar",
    currentSlug: "solar-inverter-repair",
    heroTitle: "Solar Inverter Repair in Southern California",
    heroSubtitle: "Inverter not working? We'll replace it with a manufacturer warranty and become your ongoing service provider - no more chasing a company that won't call back.",
    intro: "The inverter is the piece of your solar system most likely to fail, and when it does, the whole system usually stops producing. We diagnose it fast and get you back online with real warranty coverage.",
    sections: [
      {
        title: "Inverter not working? Here's what we do",
        body: [
          "If your inverter is dead, cycling errors, or just not putting out what it used to, we test it on site and give you a straight answer. If it needs replacing, we'll put in a new unit backed by a manufacturer warranty - not a used or refurbished swap.",
          "From there we become your ongoing service provider. You get one company that knows your system and answers the phone, instead of starting over with a new installer every time something comes up.",
        ],
      },
      {
        title: "Why inverters fail",
        body: [
          "String inverters and micro-inverters both take a beating from heat, especially mounted on a hot roof or garage wall in Southern California summers. Years of thermal cycling wears out internal components even on a well-built unit.",
          "Power surges, moisture intrusion into the enclosure, and simply reaching the end of a normal 10-15 year service life are the other common causes. Error codes on the display or app are usually the first sign something's going.",
        ],
      },
      {
        title: "Diagnosing before replacing",
        body: [
          "We don't replace an inverter on a hunch. We check the DC input from the panels, the AC output side, disconnects, and wiring at the inverter before deciding the unit itself is the problem versus a string fault or a wiring issue feeding it.",
          "That matters because sometimes what looks like inverter failure is actually rodent-chewed wiring or a bad connector upstream, and replacing the inverter alone wouldn't fix it.",
        ],
      },
      {
        title: "Micro-inverters vs. string inverters",
        body: [
          "A failed micro-inverter usually only takes out one panel, so the rest of the array keeps producing while we source the replacement. A failed string inverter can take the whole system down at once, which is why we treat those calls as urgent.",
          "We carry knowledge of both major inverter types used across Southern California installs and can source replacement units even when the original installer used a brand that's since exited the residential market.",
        ],
      },
      {
        title: "What's included in an inverter repair visit",
        body: [
          "Full diagnostic testing, the physical swap if replacement is needed, re-commissioning the system with your monitoring platform, and confirmation that production is back to where it should be before we leave.",
          "If the inverter swap turns up a roof penetration issue at the mounting points or chewed wiring nearby, we handle that in the same visit instead of sending you to someone else.",
        ],
      },
      {
        title: "Taking over systems from installers who disappeared",
        body: [
          "Plenty of homeowners come to us with an inverter that's failed and an installer who's out of business. We service and replace inverters on systems we didn't originally put in, and we'll get you set up with a real warranty going forward.",
        ],
      },
    ],
  },

  "solar-monitoring-repair": {
    type: "solar",
    currentSlug: "solar-monitoring-repair",
    heroTitle: "Solar Monitoring Repair in Southern California",
    heroSubtitle: "App showing nothing, or numbers that don't add up? We get your solar monitoring back online so you can actually see what your system is doing.",
    intro: "Monitoring going dark doesn't always mean the system stopped producing, but it does mean you've lost the ability to catch a real problem before it costs you money. We fix the monitoring and check the production behind it.",
    sections: [
      {
        title: "Common monitoring problems we fix",
        body: [
          "Monitoring offline is one of the most common calls we get - the gateway or communication card loses connection to Wi-Fi or cellular, and the app just stops updating. Sometimes it's a router change at the house, sometimes it's a failed communication module at the inverter.",
          "Other times the app is live but the numbers look wrong - a panel or string reading zero, production that doesn't match what the weather and time of year should give you, or data that's frozen on an old reading.",
        ],
      },
      {
        title: "Why we check production, not just the app",
        body: [
          "A dark monitoring app can mean two very different things - the system is fine and just can't report data, or the system has an actual fault and the monitoring failure is hiding it. We don't assume it's the harmless version.",
          "We check real production at the meter and inverter alongside the monitoring hardware itself, so you know for certain whether you've been losing power output while the app sat dark.",
        ],
      },
      {
        title: "What causes monitoring to fail",
        body: [
          "Router and network changes at the house, a failed communication card at the inverter, firmware that's gone stale, or physical damage to the gateway device are the usual suspects. Occasionally rodent damage to wiring near the inverter takes out the monitoring connection along with other circuits.",
          "Older systems also run on monitoring platforms and hardware that manufacturers have since discontinued, which can mean a full communication module replacement instead of a simple reset.",
        ],
      },
      {
        title: "What a monitoring repair visit includes",
        body: [
          "We test the communication gateway, confirm network connectivity, check firmware, and verify production data matches actual meter readings. If hardware needs replacing, we install a current module and get your account reconnected to the manufacturer's platform.",
          "We also walk you through reading your own monitoring app afterward, so you can spot a real drop in production yourself instead of finding out from a surprise utility bill.",
        ],
      },
      {
        title: "Systems installed by companies that closed up shop",
        body: [
          "Monitoring is one of the first things to break down on systems from installers who went out of business, since the original monitoring account or portal often gets orphaned. We reconnect these systems where possible or set up an updated monitoring solution so you're not flying blind going forward.",
        ],
      },
      {
        title: "Where we work",
        body: [
          "We handle solar monitoring repair across Southern California, Coronado, Winter Gardens, Pine Valley, Poway, Carlsbad and the rest of Southern California.",
        ],
      },
    ],
  },

  "solar-panel-removal-reinstallation": {
    type: "solar",
    currentSlug: "solar-panel-removal-reinstallation",
    heroTitle: "Solar Panel Removal & Reinstallation in Southern California",
    heroSubtitle: "Detach and reset your existing array so the roof underneath can be replaced properly - one crew handling both the panels and the roofing.",
    intro: "If your roof needs work and there's solar sitting on top of it, the panels have to come off first. We handle the detach, store the equipment safely, re-roof, then set the array back and confirm it's producing again.",
    sections: [
      {
        title: "Why the panels come off before a re-roof",
        body: [
          "Roofing around a solar array never ends well. The mounting feet are attached through the existing roof, so the only way to get a clean, warrantable roof underneath is to take the array down, do the roof properly, and reinstall.",
          "Because we do both trades in house, nobody is waiting on a second contractor and nobody is pointing fingers if a leak shows up later. One company owns the roof and the array.",
        ],
      },
      {
        title: "How the detach and reset works",
        body: [
          "We shut the system down safely, label every panel and run of wiring, remove the modules and racking, and store everything on site or in our shop while the roofing work happens.",
          "Once the new roof is on, we reset the racking with new flashings at every penetration, remount the panels, reconnect the wiring, and power the system back up. Then we watch the monitoring to make sure production is back where it should be.",
        ],
      },
      {
        title: "What we check while the array is down",
        body: [
          "With the panels off, we get a rare look at hardware that's normally hidden - connectors, conduit runs, rodent damage under the array, and any panel that's already failing. If something needs replacing, this is the cheapest time to do it.",
          "We'll tell you what we find and what it costs, and if the array is old enough that replacement makes more sense than a reset, we'll say that too instead of putting tired equipment back on a brand new roof.",
        ],
      },
      {
        title: "Where we work",
        body: [
          "We handle solar panel removal and reinstallation across Southern California, from San Diego County through Orange County, Riverside and the greater Los Angeles area.",
        ],
      },
    ],
  },
};
